import { useMutation, useQueryClient } from "@tanstack/react-query";
import { ButtonPrimary } from "components/Atoms/Button";
import { TextArea } from "components/Atoms/formInputs/TextArea";
import { Loading, Panel } from "components/panels";
import Pagination from "pagination/components/pagination";
import { AdvancingSessionApi } from "queries/AdvancingSession";
import { useInvalidateOnAdvancingActivity } from "../activity/useInvalidateOnAdvancingActivity";
import React, { useEffect, useRef, useState } from "react";
import { AdvancingChatRow } from "./AdvancingChatRow";
import { advancingMessageMutationKey, getAdvancingMessagesQueryKey, useMarkAdvancingMessageSeen } from "./useMarkAdvancingMessageSeen";

const perPage = 10;

interface Props {
  advancingSessionId: number
  incomingMessageType: server.dto.AdvancingMessageTypes
}

export const AdvancingChatPanel = (props: Props) => {
  const queryClient = useQueryClient();
  const [page, setPage] = useState(1);
  const [body, setBody] = useState("");
  const outgoingMessageType: server.dto.AdvancingMessageTypes = props.incomingMessageType === "toAgent" ? "fromAgent" : "toAgent";

  const messagesQuery = AdvancingSessionApi.PaginatedMessage.useQuery({ advancingSessionId: props.advancingSessionId, page, perPage }, { refetchOnWindowFocus: true });
  const markSeenMutation = useMarkAdvancingMessageSeen(props.advancingSessionId);
  useInvalidateOnAdvancingActivity(props.advancingSessionId, getAdvancingMessagesQueryKey(props.advancingSessionId), { skipWhileMutating: advancingMessageMutationKey });
  const messagesRef = useRef<HTMLDivElement>(null);
  const newestMessageId = messagesQuery.data?.data[0]?.advancingMessageId;

  // Only when the newest message changes, so marking a message seen keeps the scroll position
  useEffect(() => {
    if (!messagesRef.current) return;
    messagesRef.current.scrollTop = messagesRef.current.scrollHeight;
  }, [newestMessageId]);

  const sendMutation = useMutation({
    mutationKey: advancingMessageMutationKey,
    mutationFn: (instruction: server.dto.SendAdvancingMessageInstruction) => window.CoreApi.AdvancingSession.SendMessage(instruction, { toastOnError: true }).then(r => r.data),
    onSuccess: async response => {
      if (response.hasErrors) return;
      setBody("");
      setPage(1);
      await queryClient.invalidateQueries({ queryKey: getAdvancingMessagesQueryKey(props.advancingSessionId) });
    }
  });

  const send = () => {
    if (sendMutation.isLoading || !body.trim()) return;
    sendMutation.mutate({ advancingSessionId: props.advancingSessionId, body, messageType: outgoingMessageType });
  };

  return <Panel
    panelClassName="s1_panel"
    title="Chat"
    contentCssClass="flex-column gap-larger"
    pagination={<Pagination
      small
      onPaginate={setPage}
      paginationData={{
        page,
        perPage,
        total: messagesQuery.data?.pagination.total ?? 0,
        pageCount: messagesQuery.data?.pagination.pageCount ?? 0,
        maxPerPage: perPage
      }}
    />}
  >
    {messagesQuery.data
      ? messagesQuery.data.data.length > 0 && <div ref={messagesRef} className="advancing-chat-messages overflow-y flex-column gap-small">
        {/* The server returns the newest first, the newest is shown closest to the message box */}
        {[...messagesQuery.data.data].reverse().map(message => <AdvancingChatRow
          key={message.advancingMessageId}
          message={message}
          isIncoming={message.messageType === props.incomingMessageType}
          isMarkingSeen={markSeenMutation.isLoading && markSeenMutation.variables === message.advancingMessageId}
          onMarkSeen={() => {
            if (markSeenMutation.isLoading) return;
            markSeenMutation.mutate(message.advancingMessageId);
          }}
        />)}
      </div>
      : <Loading />}
    <div className="flex-column gap">
      <TextArea
        value={body}
        onChange={setBody}
        placeholder={RESX.Advancing.WriteAMessage}
        maxLength={serverReference.constants.advancing.maxMessageBodyLength}
        rows={4}
        onCtrlEnter={send}
      />
      <div className="flex justify-space-between align-center">
        <div className="flex align-center gap" />
        <ButtonPrimary label={RESX.Buttons.btnSend} onClick={send} loading={sendMutation.isLoading} disabled={!body.trim()} />
      </div>
    </div>
  </Panel>;
};
